import React, { memo, Suspense, useState, useCallback, useMemo } from "react";
import _ from "lodash";
import StudentDetailsLayout from "../../../components/StudentDetailsLayout/StudentDetailsLayout";
import HeaderRightComponent from "./HeaderRightComponent";
import classes from "./AcademicRecords.scss";
import {
  useFetchAcademicRecordQueries,
  useSyncTranscriptEntity,
} from "../hooks";
import { useDispatch } from "Hooks";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import StudentPersonalDetailsSkeleton from "./StudentPersonalDetailsSkeleton";
import StudentTranscriptCommentSkeleton from "./StudentTranscriptCommentSkeleton";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import SkeletonLoaderDataGrid from "./SkeletonLoaderDataGrid";
import AcademicRecordsModal from "./AcademicRecordsModal";
import { AcademicRecordsModalProvider } from "../context";
import {
  getStudentPersonalDetailsQuery,
  getStudentTranscriptAcademicRecordsQuery,
  getTranscriptResourcesQuery,
} from "../module/AcademicRecordsQueries";

const AcademicRecords = React.lazy(() => import("./AcademicRecords"));

const AcademicRecordsWrapper = ({
  params: { studentId },
  router,
  route,
  currentStudent,
  previousStudentId,
  nextStudentId,
  onStudentClick,
  students,
}) => {
  const {
    academicRecordsQueryRef,
    commentFieldQueryRef,
    customFieldConfigQueryRef,
    personalDetailsQueryRef,
    transcriptResourcesQueryRef,
  } = useFetchAcademicRecordQueries(studentId);

  const dispatch = useDispatch();
  const { syncEntity } = useSyncTranscriptEntity();
  const [syncingEntities, setSyncingEntities] = useState({});

  const handleSync = useCallback(
    async ({ entityType, entityId }) => {
      const refetchQueries =
        entityType === "STUDENT_BASIC_DETAILS"
          ? [getStudentPersonalDetailsQuery]
          : [
              getStudentTranscriptAcademicRecordsQuery,
              getTranscriptResourcesQuery,
            ];

      setSyncingEntities(prev => ({ ...prev, [entityId]: true }));
      try {
        await syncEntity({ entityType, entityId, refetchQueries });
        dispatch(
          setToastMsg({
            msg: "toastMsgs:planner_elements_synced_successfully_with_label",
            locale_params: [
              {
                key: "label",
                value: "common:data",
                isPlainText: false,
              },
            ],
            type: "success",
          })
        );
      } finally {
        setSyncingEntities(prev => ({ ...prev, [entityId]: false }));
      }
    },
    [syncEntity, dispatch]
  );
  const sidebarStudentIds = useMemo(() => _.map(students, "id"), [students]);

  return (
    <AcademicRecordsModalProvider>
      <StudentDetailsLayout
        childrenClass={classes.childrenClass}
        headerRightComponent={
          <Suspense fallback={<SkeletonLoader height="36px" width="108px" />}>
            <HeaderRightComponent
              studentId={studentId}
              currentStudent={currentStudent}
              commentFieldQueryRef={commentFieldQueryRef}
              academicRecordsQueryRef={academicRecordsQueryRef}
              router={router}
              route={route}
              sidebarStudentIds={sidebarStudentIds}
            />
          </Suspense>
        }
        currentStudent={currentStudent}
        previousStudentId={previousStudentId}
        nextStudentId={nextStudentId}
        onStudentClick={onStudentClick}
      >
        <Suspense
          fallback={
            <>
              <StudentPersonalDetailsSkeleton />
              <SkeletonLoaderDataGrid />
              <StudentTranscriptCommentSkeleton />
            </>
          }
        >
          <AcademicRecords
            studentId={studentId}
            currentStudent={currentStudent}
            customFieldConfigQueryRef={customFieldConfigQueryRef}
            personalDetailsQueryRef={personalDetailsQueryRef}
            commentFieldQueryRef={commentFieldQueryRef}
            academicRecordsQueryRef={academicRecordsQueryRef}
            transcriptResourcesQueryRef={transcriptResourcesQueryRef}
            syncingEntities={syncingEntities}
          />
        </Suspense>
      </StudentDetailsLayout>
      <AcademicRecordsModal
        academicRecordsQueryRef={academicRecordsQueryRef}
        currentStudent={currentStudent}
        onSync={handleSync}
      />
    </AcademicRecordsModalProvider>
  );
};

export default memo(AcademicRecordsWrapper);
